import React, { useState, useEffect } from 'react';
import { usersAPI } from '../services/api';

interface UserModalProps {
  uuid: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ uuid, isOpen, onClose, onSave }) => {
  const [user, setUser] = useState<any>(null);
  const [form, setForm] = useState({
    username: '',
    email: '',
    role: 'user',
    registration: false,
    is_disabled: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isCreating = !uuid;

  useEffect(() => {
    if (isOpen && uuid) {
      setError('');
      setLoading(true);
      usersAPI.getById(uuid).then(res => {
        setUser(res.data.user);
        setForm({
          username: res.data.user.username || '',
          email: res.data.user.email || '',
          role: res.data.user.role || 'user',
          registration: !!res.data.user.registration,
          is_disabled: !!res.data.user.is_disabled,
        });
      }).catch(() => {
        setError('Failed to load user');
      }).finally(() => setLoading(false));
    } else if (isOpen && !uuid) {
      // Creating new user
      setUser(null);
      setForm({ 
        username: '', 
        email: '', 
        role: 'user', 
        registration: false, 
        is_disabled: false 
      });
      setError('');
    }
  }, [isOpen, uuid]);

  // Auto-dismiss error after 10 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setForm({ ...form, [name]: checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSave = async () => {
    setError('');
    setLoading(true);
    
    try {
      if (isCreating) {
        // Create new user (password/PIN auto-generated by backend)
        await usersAPI.create(form);
        onSave();
        onClose();
      } else {
        // Update existing user (exclude username from update)
        const { username, ...updateData } = form;
        await usersAPI.update(uuid!, updateData);
        onSave();
        onClose();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || (isCreating ? 'Failed to create user.' : 'Failed to save user.');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4">{isCreating ? 'Create New User' : 'Edit User'}</h2>
        {loading && uuid ? (
          <div className="text-center py-8 text-primary-600">Loading...</div>
        ) : (
          <form className="space-y-4" onSubmit={e => { e.preventDefault(); handleSave(); }}>
            {isCreating && (
              <div>
                <label className="block text-sm font-semibold mb-1">Username <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  name="username" 
                  value={form.username} 
                  onChange={handleChange} 
                  className="w-full px-4 py-2 border rounded-lg" 
                  required 
                  placeholder="Enter username"
                />
              </div>
            )}
            {!isCreating && user && (
              <div>
                <label className="block text-sm font-semibold mb-1">Username</label>
                <input 
                  type="text" 
                  value={user.username} 
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100" 
                  disabled 
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold mb-1">Email <span className="text-red-500">*</span></label>
              <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg" required />
            </div>
            {isCreating && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Auto-Generated Password:</strong> A secure 6-digit PIN will be automatically generated and sent to the user's email if the account is enabled.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold mb-1">Role</label>
              <select name="role" value={form.role} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg">
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" name="registration" checked={form.registration} onChange={handleChange} />
              <label className="text-sm">
                Registration:
                {form.registration
                  ? <span className="ml-2 text-green-600 font-bold">Yes</span>
                  : <span className="ml-2 text-red-600 font-bold">No</span>
                }
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" name="is_disabled" checked={form.is_disabled} onChange={handleChange} />
              <label className="text-sm">
                Disabled:
                {form.is_disabled
                  ? <span className="ml-2 text-red-600 font-bold">Yes</span>
                  : <span className="ml-2 text-green-600 font-bold">No (Active)</span>
                }
              </label>
            </div>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="px-4 py-2 rounded-lg bg-gray-200" onClick={onClose}>Cancel</button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold" disabled={loading}>
                {loading ? 'Saving...' : (isCreating ? 'Create User' : 'Save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserModal;

